/**
 * "Report outcome" — two steps, then what it means for the user.
 *
 *   1. How did this deal end?            sold / refinanced (new appraisal) / didn't close
 *   2. The amount (sold, refi) or why (didn't close)
 *   →  "Your analysis predicted $X. Real result: $Y. It landed within Z%."
 *      (no_deal: "Thanks, recorded." — there is no number to compare)
 *
 * All copy comes from utils/outcomeText.js, which is what the tests pin. The miss is
 * shown as a magnitude; the sign lives in the database, not on this screen.
 */
import React, { useState } from 'react';
import { X } from 'lucide-react';
import { analysisOutcomeAPI } from '../api';
import {
  step1Text, step2Text, resultText, uiText, buildOutcomePayload, OUTCOME_TYPES, NO_DEAL_REASONS,
} from '../utils/outcomeText';

export default function ReportOutcomeModal({ analysisId, address, lang, existing, onClose, onSaved }) {
  const t = uiText(lang);
  const s1 = step1Text(lang);
  const [type, setType] = useState(existing?.type || '');
  const [amount, setAmount] = useState(existing?.amount ? String(existing.amount) : '');
  const [date, setDate] = useState(existing?.date || '');
  const [reason, setReason] = useState(existing?.no_deal_reason || '');
  const [step, setStep] = useState(1);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  const s2 = type ? step2Text(type, lang) : null;

  const submit = async () => {
    const built = buildOutcomePayload({ type, amount, date, reason }, lang);
    if (built.error) { setError(built.error); return; }
    setError('');
    setSaving(true);
    try {
      const r = await analysisOutcomeAPI.report(analysisId, built.payload);
      const data = r.data || {};
      setResult({ outcome: data.outcome, comparison: data.comparison, trackRecord: data.track_record });
      if (onSaved) onSaved(data.outcome);
    } catch (e) {
      setError(t.failed);
    } finally {
      setSaving(false);
    }
  };

  const done = result ? resultText(result.outcome?.type || type, result.comparison, result.trackRecord, lang) : null;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
      data-testid="report-outcome-modal"
    >
      <div
        className="bg-white rounded-xl shadow-2xl max-w-md w-full p-5 sm:p-6"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="flex items-start justify-between gap-3 mb-1">
          <h2 className="text-lg font-semibold text-slate-900">
            {done ? done.title : s1.title}
          </h2>
          <button type="button" onClick={onClose} aria-label={t.close} className="text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        {address && <p className="text-xs text-slate-500 truncate mb-4">{address}</p>}

        {done ? (
          <div data-testid="outcome-result">
            {done.lines.map((line, i) => (
              <p key={i} className={`text-sm ${i === 1 ? 'font-semibold text-slate-900 mt-2' : 'text-slate-700'}`}>{line}</p>
            ))}
            <button
              type="button"
              onClick={onClose}
              className="mt-5 w-full px-4 py-2 bg-secondary text-white rounded-lg text-sm font-medium hover:bg-blue-700"
            >
              {t.close}
            </button>
          </div>
        ) : step === 1 ? (
          <div>
            <p className="text-sm text-slate-600 mb-4">{s1.lead}</p>
            <div className="space-y-2">
              {OUTCOME_TYPES.map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => { setType(k); setError(''); setStep(2); }}
                  className={`w-full text-left px-4 py-3 rounded-lg border text-sm font-medium transition-colors ${
                    type === k ? 'border-secondary bg-blue-50 text-slate-900' : 'border-slate-200 hover:border-secondary hover:bg-blue-50 text-slate-800'
                  }`}
                  data-testid={`outcome-type-${k}`}
                >
                  {s1.options[k]}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div>
            <p className="text-xs text-slate-500 mb-3">{s1.options[type]}</p>
            {type === 'no_deal' ? (
              <div>
                <p className="text-sm font-medium text-slate-800 mb-2">{s2.reasonLabel}</p>
                <div className="space-y-2">
                  {NO_DEAL_REASONS.map((k) => (
                    <button
                      key={k}
                      type="button"
                      onClick={() => { setReason(k); setError(''); }}
                      className={`w-full text-left px-4 py-2.5 rounded-lg border text-sm transition-colors ${
                        reason === k ? 'border-secondary bg-blue-50 text-slate-900' : 'border-slate-200 hover:border-secondary text-slate-800'
                      }`}
                      data-testid={`no-deal-reason-${k}`}
                    >
                      {s2.reasons[k]}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <label className="block">
                  <span className="text-sm font-medium text-slate-800">{s2.amountLabel}</span>
                  <div className="mt-1 flex items-center rounded-lg border border-slate-300 focus-within:border-secondary px-3">
                    <span className="text-slate-500 text-sm">$</span>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={amount}
                      onChange={(e) => { setAmount(e.target.value); setError(''); }}
                      className="w-full py-2 px-2 text-sm outline-none"
                      placeholder="315,000"
                      data-testid="outcome-amount"
                      autoFocus
                    />
                  </div>
                </label>
                <label className="block">
                  <span className="text-sm text-slate-600">{s2.dateLabel}</span>
                  <input
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                    data-testid="outcome-date"
                  />
                </label>
              </div>
            )}
            {error && <p className="mt-3 text-sm text-red-600" data-testid="outcome-error">{error}</p>}
            <div className="mt-5 flex items-center justify-between gap-3">
              <button type="button" onClick={() => { setStep(1); setError(''); }} className="text-sm text-slate-600 hover:text-slate-800">
                {t.back}
              </button>
              <button
                type="button"
                onClick={submit}
                disabled={saving}
                className="px-4 py-2 bg-secondary text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-60"
                data-testid="outcome-save"
              >
                {saving ? t.saving : t.save}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
